/**
 * Authentication and session endpoints — `docs/API.md` §2.
 *
 * Mirrors `salescrmapi/src/modules/auth`. Each function is one endpoint; no
 * function here decides anything, so the contract stays readable as a list.
 */
import apiClient from './axiosClient.js';

/**
 * Exchange an email and password for a session.
 *
 * Returns **one of three** shapes, all with status 200, because the login screen
 * cannot know which flow applies before the user has typed anything:
 *
 *  - `{ access_token, refresh_token, user, roles, landing_screen, … }` — signed in
 *  - `{ mfa_required: true, challenge_token, factors, expires_in }`
 *  - `{ sso_required: true, provider, authorize_url }`
 *
 * The caller must branch on the body, not on the status. Throws `ApiError` with
 * `invalid_credentials` for both an unknown address and a wrong password — the
 * API answers those identically, and in the same time, so the endpoint cannot be
 * used to discover which addresses have accounts.
 *
 * @param {{email:string, password:string, remember_me?:boolean, device_label?:string}} credentials
 */
export async function login(credentials) {
  const { data } = await apiClient.post('/auth/login', {
    ...credentials,
    device_label: credentials.device_label ?? deviceLabel(),
  });
  return data;
}

/**
 * Complete an MFA challenge issued by `login` and upgrade it to a real session.
 *
 * Currently answers `501 not_implemented`: the API stores a pointer to a secrets
 * manager in `iam.mfa_factor.secret_ref` and none is configured. The challenge
 * lifecycle around it — expiry, attempt counting, burn after `max_attempts` — is
 * live, so a wrong code still consumes an attempt.
 *
 * @param {{challenge_token:string, factor_id:string, code?:string, assertion?:object}} challenge
 */
export async function verifyMfa(challenge) {
  const { data } = await apiClient.post('/auth/mfa/verify', challenge);
  return data;
}

/**
 * Trade a refresh token for a new access token.
 *
 * Normally called by the axios interceptor rather than by a screen. Refresh
 * tokens rotate; presenting a spent one revokes the whole session, so never call
 * this twice with the same token.
 *
 * @param {string} refreshToken
 */
export async function refresh(refreshToken) {
  const { data } = await apiClient.post('/auth/refresh', { refresh_token: refreshToken });
  return data;
}

/**
 * End the current session, or every session this user has.
 * @param {boolean} [allDevices=false] revoke everywhere, not just here
 */
export async function logout(allDevices = false) {
  await apiClient.post('/auth/logout', { all_devices: allDevices });
}

/**
 * Who am I, what may I open, what may I do.
 *
 * The bootstrap call every client makes on load. `screens` is what the sidebar
 * renders from — hard-coding navigation instead is what lets the UI and the
 * permission model drift apart. A screen the caller may see but do nothing on
 * appears with an empty `operations` array, which is different from the screen
 * being absent.
 */
export async function getMe() {
  const { data } = await apiClient.get('/auth/me');
  return data;
}

/**
 * The caller's active sessions, so they can spot a sign-in they do not recognise.
 * Exactly one carries `is_current: true`.
 */
export async function listSessions() {
  const { data } = await apiClient.get('/auth/sessions');
  return data.data;
}

/**
 * End one session by id. A user may always end their own; ending someone else's
 * needs `admin.security:edit`.
 * @param {string} sessionId
 */
export async function revokeSession(sessionId) {
  await apiClient.delete(`/auth/sessions/${sessionId}`);
}

/**
 * Ask for a password-reset link.
 *
 * Always resolves, with the same body and timing whether or not the address
 * exists — so the UI must show the same confirmation either way rather than
 * "we've sent you a link" only on a hit.
 *
 * @param {string} email
 */
export async function forgotPassword(email) {
  const { data } = await apiClient.post('/auth/password/forgot', { email });
  return data;
}

/**
 * Consume a reset token and set a new password. Every session for that user is
 * revoked, including any the attacker may hold, which is the point of resetting.
 *
 * Throws `password_policy_violation` with `details` naming the rule that failed,
 * or `password_recently_used` when the candidate is inside the org's history depth.
 *
 * @param {{token:string, new_password:string}} payload
 */
export async function resetPassword(payload) {
  await apiClient.post('/auth/password/reset', payload);
}

/**
 * Change your own password while signed in. Other sessions are revoked; the
 * current one survives, so the user is not signed out of the tab they are in.
 *
 * @param {{current_password:string, new_password:string}} payload
 */
export async function changePassword(payload) {
  await apiClient.post('/auth/password/change', payload);
}

/**
 * A human-readable name for this browser, stored on the session so the "where am
 * I signed in" list reads as somewhere a person recognises rather than a user-agent
 * string.
 */
function deviceLabel() {
  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /Firefox\//.test(ua) ? 'Firefox'
    : 'Browser';
  const os =
    /Mac/.test(ua) ? 'macOS'
    : /Win/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS'
    : '';
  return os ? `${browser} on ${os}` : browser;
}
